import { RefObject, useEffect } from 'react';

// Keyboard behaviour for a drawer or modal that claims aria-modal="true".
//
// The claim is that the rest of the page is inert to assistive tech, so the
// focus behaviour has to match it: move focus in when it opens, keep Tab
// inside, close on Escape, and hand focus back to whatever opened it.
//
// Shared by the Pick throughput drawer and the Suggested hours drawer, which
// carried the same effect twice.
//
// `onClose` must be stable across renders — an inline arrow makes the effect
// re-run on every parent render, which re-steals focus mid-interaction. Wrap
// it in useCallback at the call site.
// Whether closing the panel should hand focus back to whatever opened it.
//
// Two shapes of close land differently, and both have to restore. A panel that
// unmounts runs the cleanup below while its DOM is still attached and still
// focused; a panel that merely re-renders closed has already dropped focus to
// body. Anything else holding focus — the warehouse selector or the week arrow
// a planner just clicked, which closes the panel as a side effect — must not
// have it yanked away.
//
// Exported because jsdom detaches the DOM before cleanup runs, so the unmount
// case cannot be reproduced through a render test; this predicate is where it
// gets pinned.
export const shouldRestoreFocus = (panel: HTMLElement | null): boolean =>
  document.activeElement === document.body ||
  Boolean(panel?.contains(document.activeElement));

export const useFocusTrap = (
  ref: RefObject<HTMLElement>,
  { isOpen, onClose }: { isOpen: boolean; onClose: () => void },
): void => {
  useEffect(() => {
    if (!isOpen) return;
    const opener = document.activeElement as HTMLElement | null;
    const focusable = (): HTMLElement[] =>
      Array.from(
        ref.current?.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      ).filter((el) => !el.hasAttribute('disabled'));

    focusable()[0]?.focus();

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = focusable();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const inside = ref.current?.contains(document.activeElement);
      if (e.shiftKey && (!inside || document.activeElement === first)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (!inside || document.activeElement === last)) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return (): void => {
      window.removeEventListener('keydown', onKey);
      if (shouldRestoreFocus(ref.current)) opener?.focus();
    };
  }, [isOpen, onClose, ref]);
};
